import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  BarChart3, Users, AlertTriangle, Send, Shield, 
  Search, CheckCircle, XCircle, MessageSquare, 
  Activity, MapPin, Zap, Loader2, Database, Server, 
  Smartphone, Image as ImageIcon, TrendingUp, Menu,
  Filter, MoreVertical, Bell, LineChart, Lock
} from 'lucide-react';
import { FamilyMember, Post } from '../types';
import { doc, setDoc, collection, onSnapshot, query, orderBy, updateDoc, writeBatch, where } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import Analytics from './Analytics';

interface AdminProps {
  members: Record<string, FamilyMember>;
  posts: Post[];
  currentUser?: FamilyMember;
  onDeletePost: (id: string) => void;
  onDeleteMember?: (id: string) => void;
}

const AdminDashboard: React.FC<AdminProps> = ({ members, posts, currentUser, onDeletePost, onDeleteMember }) => {
  const [activeView, setActiveView] = useState('insights');
  
  // --- STATE: BROADCAST ---
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [targetAudience, setTargetAudience] = useState('all');
  const [messageType, setMessageType] = useState<'info' | 'warning' | 'alert'>('info');
  const [isSending, setIsSending] = useState(false);

  // --- STATE: MERGE FAMILIES ---
  const [mergeSearchMember1, setMergeSearchMember1] = useState('');
  const [mergeSearchMember2, setMergeSearchMember2] = useState('');
  const [selectedMember1, setSelectedMember1] = useState<FamilyMember | null>(null);
  const [selectedMember2, setSelectedMember2] = useState<FamilyMember | null>(null);
  const [mergeRelationType, setMergeRelationType] = useState<string>('husband-wife');
  const [isMerging, setIsMerging] = useState(false);
  const [mergeLog, setMergeLog] = useState<any[]>([]);

  // --- STATE: DATA ---
  const [complaints, setComplaints] = useState<any[]>([]);
  // Mock Admin Requests (Local state for demo purposes)
  const [adminRequests, setAdminRequests] = useState([
    { id: 'req1', name: 'Uncle John', phone: '+919876543210', date: '2025-12-25', approvals: 1, status: 'Pending' },
    { id: 'req2', name: 'Aunt Sarah', phone: '+919876543211', date: '2025-12-26', approvals: 0, status: 'Pending' }
  ]);

  // --- SECURITY CONFIGURATION ---
  // List of allowed phone numbers (10 digits)
  const SUPER_ADMINS = ["9620211079", "9611638031"];

  // --- SECURITY CHECK ---
  // 1. Normalize current user's phone number
  const userPhone = currentUser?.phoneNumber?.replace(/\D/g, '').slice(-10) || '';
  
  // 2. Check authorization
  const isAuthorized = SUPER_ADMINS.includes(userPhone);
  
  // Debug logging
  useEffect(() => {
    console.log('AdminDashboard Authorization Debug:', {
      hasCurrentUser: !!currentUser,
      currentUserId: currentUser?.id,
      currentUserName: currentUser?.name,
      rawPhone: currentUser?.phoneNumber,
      normalizedPhone: userPhone,
      isPhoneEmpty: userPhone === '',
      superAdmins: SUPER_ADMINS,
      isInArray: SUPER_ADMINS.includes(userPhone),
      isAuthorized,
      checkMessage: userPhone ? `Phone "${userPhone}" ${isAuthorized ? '✅ AUTHORIZED' : '❌ NOT AUTHORIZED'}` : '❌ No phone number'
    });
  }, [currentUser, userPhone, isAuthorized]);

  // --- 1. REAL-TIME DATABASE LISTENERS ---
  // Only runs if guard clause passes
  useEffect(() => {
    // Listen to the real 'complaints' collection filtered by family
    const familyId = currentUser?.familyId;
    if (!familyId) return;
    
    const q = query(
      collection(db, "complaints"),
      where("familyId", "==", familyId),
      orderBy("timestamp", "desc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const loadedComplaints = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setComplaints(loadedComplaints);
    });
    return () => unsubscribe();
  }, [currentUser?.familyId]);

  // --- 2. ADVANCED STATS ENGINE ---
  const stats = useMemo(() => {
    const memberList: FamilyMember[] = Object.values(members);
    const totalMembers = memberList.length || 1; 
    const totalConnections = memberList.reduce((acc, m) => acc + (m.connections?.length || 0), 0);
    const activePosters = new Set(posts.map(p => p.authorId)).size;
    
    // Mock Generational Logic (In real app, parse birthdays)
    const genZ = memberList.filter((_, i) => i % 3 === 0).length;
    const millennials = memberList.filter((_, i) => i % 3 === 1).length;
    const boomers = memberList.filter((_, i) => i % 3 === 2).length;

    const mediaPosts = posts.filter(p => p.content.includes('http') || p.imageUrl).length;
    
    const unmarriedMales = memberList.filter(m => m.gender === 'Male' && !m.isMarried).length;
    const unmarriedFemales = memberList.filter(m => m.gender === 'Female' && !m.isMarried).length;
    const unmarriedOthers = memberList.filter(m => m.gender === 'Other' && !m.isMarried).length;

    // Gender Breakdown
    const genderBreakdown = {
      males: memberList.filter(m => m.gender === 'Male').length,
      females: memberList.filter(m => m.gender === 'Female').length,
      others: memberList.filter(m => m.gender === 'Other').length
    };

    // Marital Status Breakdown
    const maritalBreakdown = {
      single: memberList.filter(m => m.maritalStatus === 'Single' || (!m.maritalStatus && !m.isMarried)).length,
      married: memberList.filter(m => m.maritalStatus === 'Married' || (!m.maritalStatus && m.isMarried)).length,
      divorced: memberList.filter(m => m.maritalStatus === 'Divorced').length
    };

    return {
        totalMembers,
        totalPosts: posts.length,
        cities: new Set(memberList.map(m => m.location).filter(Boolean)).size,
        networkDensity: ((totalConnections / totalMembers).toFixed(1)),
        engagementRate: Math.round((activePosters / totalMembers) * 100),
        pendingComplaints: complaints.filter(c => c.status === 'Pending').length,
        genBreakdown: { genZ, millennials, boomers },
        contentMix: { 
            media: Math.round((mediaPosts / (posts.length || 1)) * 100),
            text: 100 - Math.round((mediaPosts / (posts.length || 1)) * 100)
        },
        unmarriedMales,
        unmarriedFemales,
        unmarriedOthers,
        genderBreakdown,
        maritalBreakdown
    };
  }, [members, posts, complaints]);

  // --- FAMILY INSIGHTS CALCULATION ---
  const familyInsights = useMemo(() => {
    const memberList = Object.values(members) as FamilyMember[];
    
    // Group members by familyId
    const familiesByid: Record<string, FamilyMember[]> = {};
    memberList.forEach((member: FamilyMember) => {
      const fid = member.familyId || 'unknown';
      if (!familiesByid[fid]) {
        familiesByid[fid] = [];
      }
      familiesByid[fid].push(member);
    });

    // Create sorted array: by member count (descending)
    const familyList = Object.entries(familiesByid)
      .map(([familyId, members]) => ({
        familyId,
        memberCount: members.length,
        members: members
      }))
      .sort((a, b) => b.memberCount - a.memberCount);

    return {
      totalFamilies: familyList.length,
      families: familyList
    };
  }, [members]);

  // --- 3. ACTIONS & HANDLERS ---

  const handleBroadcast = async () => {
    if (!broadcastMsg.trim()) return;
    setIsSending(true);
    try {
        const isTargeted = targetAudience !== 'all' && targetAudience !== 'admins';
        const targetUser = isTargeted ? members[targetAudience] : null;
        const familyId = currentUser?.familyId || members[Object.keys(members)[0]]?.familyId || 'system';

        const newPost: any = {
            id: `broadcast-${Date.now()}`,
            authorId: 'system-official',
            firebaseUid: 'system-official',
            familyId: familyId,
            content: broadcastMsg,
            timestamp: new Date().toISOString(),
            type: 'status',
            privacy: isTargeted ? 'specific' : 'public',
            targetAudienceIds: isTargeted ? [targetAudience] : [],
            likes: [],
            comments: 0,
            isSystemBroadcast: true
        };
        
        await setDoc(doc(db, "posts", newPost.id), newPost);

        // Create notifications for broadcast (non-critical, failure won't block broadcast)
        try {
            const recipientIds = isTargeted ? [targetAudience] : Object.keys(members).filter(id => id !== 'system-official');
            const notificationPromises = recipientIds.map(recipientId => {
                const notification: any = {
                    id: `notif-${Date.now()}-${recipientId}`,
                    recipientId: recipientId,
                    actorId: 'system-official',
                    actorName: 'Famitry Admin',
                    timestamp: new Date().toISOString(),
                    familyId: familyId,
                    type: 'admin_alert',
                    title: 'Famitry Admin',
                    message: broadcastMsg,
                    targetId: newPost.id,
                    targetType: 'post',
                    read: false,
                    dismissed: false,
                    priority: 'high',
                    pushSent: false,
                    emailSent: false
                };
                return setDoc(doc(db, "notifications", notification.id), notification).catch(err => {
                    console.warn(`Failed to create notification for ${recipientId}:`, err);
                    return null; // Don't throw, continue
                });
            });

            await Promise.all(notificationPromises);
        } catch (notifErr) {
            console.warn("Notification creation failed (non-critical):", notifErr);
            // Continue - broadcast was successful, just notifications failed
        }
        
        const successMsg = isTargeted 
          ? `Direct notification sent to ${targetUser?.name}` 
          : "Global broadcast sent to Feed";
        
        alert(successMsg); 
        setBroadcastMsg('');
    } catch (e) { 
        console.error("Broadcast error:", e); 
        alert("Failed to send broadcast. Please check console for details."); 
    } finally { 
        setIsSending(false); 
    }
  };

  const handleComplaintAction = async (reportId: string, action: 'resolve' | 'ban') => {
    try {
      const newStatus = action === 'resolve' ? 'Resolved' : 'Banned';
      await updateDoc(doc(db, "complaints", reportId), {
        status: newStatus
      });
    } catch (err) {
      console.error("Failed to update complaint:", err);
      alert("Action failed.");
    }
  };

  const handleAdminApproval = (reqId: string) => {
    setAdminRequests(prev => prev.map(req => {
        if (req.id !== reqId) return req;
        const newCount = req.approvals + 1;
        return { 
            ...req, 
            approvals: newCount,
            status: newCount >= 2 ? 'Approved' : 'Pending'
        };
    }));
  };

  // --- MERGE FAMILIES HANDLER ---
  const handleMergeFamilies = async () => {
    if (!selectedMember1 || !selectedMember2) {
      alert('Please select both members');
      return;
    }

    if (selectedMember1.familyId === selectedMember2.familyId) {
      alert('Members are already in the same family');
      return;
    }

    setIsMerging(true);
    try {
      const sourceFamilyId = selectedMember2.familyId; // Family to merge FROM
      const targetFamilyId = selectedMember1.familyId; // Family to merge INTO

      // Get all members and posts from source family
      const membersToUpdate = Object.values(members).filter(m => m.familyId === sourceFamilyId);
      const postsToUpdate = posts.filter((p: any) => p.familyId === sourceFamilyId);

      // Update all members in source family
      for (const member of membersToUpdate) {
        const memberRef = doc(db, 'members', member.id);
        await updateDoc(memberRef, { familyId: targetFamilyId });
      }

      // Update all posts in source family
      for (const post of postsToUpdate) {
        const postRef = doc(db, 'posts', post.id);
        await updateDoc(postRef, { familyId: targetFamilyId });
      }

      // Create relationship between the two members
      const batch = writeBatch(db);
      
      // Define relationship logic based on selected type
      const relationshipMap: Record<string, [string, string]> = {
        'father-son': ['childrenIds', 'parentIds'],
        'father-daughter': ['childrenIds', 'parentIds'],
        'mother-son': ['childrenIds', 'parentIds'],
        'mother-daughter': ['childrenIds', 'parentIds'],
        'husband-wife': ['spouseIds', 'spouseIds'],
        'grandfather-grandson': ['childrenIds', 'parentIds'],
        'grandfather-granddaughter': ['childrenIds', 'parentIds'],
      };

      const [field1, field2] = relationshipMap[mergeRelationType] || ['childrenIds', 'parentIds'];

      // Update member 1
      const member1Ref = doc(db, 'members', selectedMember1.id);
      const member1Updates: any = {};
      member1Updates[field1] = [...(selectedMember1[field1 as keyof FamilyMember] || []), selectedMember2.id];
      batch.update(member1Ref, member1Updates);

      // Update member 2
      const member2Ref = doc(db, 'members', selectedMember2.id);
      const member2Updates: any = {};
      member2Updates[field2] = [...(selectedMember2[field2 as keyof FamilyMember] || []), selectedMember1.id];
      
      if (mergeRelationType.includes('husband') || mergeRelationType.includes('wife')) {
        member2Updates.maritalStatus = 'Married';
        member2Updates.isMarried = true;
        member1Updates.maritalStatus = 'Married';
        member1Updates.isMarried = true;
      }
      
      batch.update(member2Ref, member2Updates);

      await batch.commit();

      // Log the merge
      const mergeRecord = {
        timestamp: new Date().toISOString(),
        admin: currentUser?.name || 'Unknown',
        member1: `${selectedMember1.name} (${selectedMember1.familyId})`,
        member2: `${selectedMember2.name} (${sourceFamilyId})`,
        relationship: mergeRelationType,
        familiesAffected: {
          from: sourceFamilyId,
          to: targetFamilyId,
          membersUpdated: membersToUpdate.length,
          postsUpdated: postsToUpdate.length
        }
      };

      setMergeLog(prev => [mergeRecord, ...prev]);
      
      alert(`✅ Merge Successful!\n\nFamily "${sourceFamilyId}" merged into "${targetFamilyId}"\n\nMembers: ${membersToUpdate.length}\nPosts: ${postsToUpdate.length}\n\nRefreshing page to display updated tree...`);
      
      // Reset form
      setSelectedMember1(null);
      setSelectedMember2(null);
      setMergeSearchMember1('');
      setMergeSearchMember2('');
      
      // Reload page to show merged tree
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error('Merge failed:', err);
      alert(`Merge failed: ${err}`);
    } finally {
      setIsMerging(false);
    }
  };

  const navItems = [
    { id: 'insights', label: 'Insights', icon: BarChart3 },
    { id: 'analytics', label: 'Analytics', icon: LineChart },
    { id: 'broadcast', label: 'Broadcast', icon: Send },
    { id: 'merge', label: 'Merge', icon: Users },
    { id: 'complaints', label: 'Reports', icon: AlertTriangle },
    { id: 'access', label: 'Access', icon: Shield },
  ];

  // --- MEMOIZED VIEW COMPONENTS (Must be at top level before guard clause) ---
  
  const BroadcastView = useCallback(() => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-in pb-20">
      <div className="lg:col-span-2 bg-white p-8 rounded-3xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center text-indigo-600"><Send className="w-5 h-5" /></div>
            <div>
                <h2 className="text-xl font-black text-gray-900">Official Communication</h2>
                <p className="text-xs text-gray-500">Send notifications to the family network</p>
            </div>
        </div>
        
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
             <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase">Recipient</label>
                <select 
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium transition-all"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                >
                  <option value="all">📢 Everyone (Global)</option>
                  <option value="admins">🛡️ All Admins</option>
                  <optgroup label="Direct Message">
                    {Object.values(members).map((m: FamilyMember) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </optgroup>
                </select>
             </div>
             <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase">Type</label>
                <select 
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium transition-all"
                  value={messageType}
                  onChange={(e) => setMessageType(e.target.value as any)}
                >
                  <option value="info">ℹ️ Info / Update</option>
                  <option value="warning">⚠️ Warning / Notice</option>
                  <option value="alert">🚨 Critical Alert</option>
                </select>
             </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase">Message Content</label>
            <textarea 
              className="w-full h-40 p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none resize-none transition-all placeholder:text-gray-400 text-sm leading-relaxed"
              placeholder="Type your official announcement here..."
              value={broadcastMsg}
              onChange={(e) => setBroadcastMsg(e.target.value)}
            ></textarea>
          </div>

          <button 
            onClick={handleBroadcast}
            disabled={isSending || !broadcastMsg.trim()}
            className={`w-full py-4 text-white rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all active:scale-[0.98] ${
               messageType === 'warning' ? 'bg-gradient-to-r from-orange-500 to-orange-600 shadow-orange-200' : 
               messageType === 'alert' ? 'bg-gradient-to-r from-red-600 to-red-700 shadow-red-200' : 
               'bg-gradient-to-r from-indigo-600 to-indigo-700 shadow-indigo-200'
            }`}
          >
            {isSending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            {isSending ? 'Dispatching...' : 'Send Official Message'}
          </button>
        </div>
      </div>

      {/* MOBILE PREVIEW */}
      <div className="bg-gray-100 p-8 rounded-3xl border border-gray-200 flex flex-col items-center justify-center relative">
        <p className="text-xs font-bold text-gray-400 mb-6 uppercase tracking-wider absolute top-6">Device Preview</p>
        <div className="w-[280px] bg-white rounded-[35px] border-8 border-gray-800 shadow-2xl overflow-hidden relative">
          {/* Mock Status Bar */}
          <div className="bg-gray-800 h-6 w-full flex justify-between items-center px-4">
             <div className="w-10 h-1 bg-gray-600 rounded-full"></div>
             <div className="flex gap-1"><div className="w-1 h-1 bg-gray-500 rounded-full"></div><div className="w-3 h-2 bg-gray-500 rounded-[1px]"></div></div>
          </div>
          
          <div className="p-4 pt-8 bg-slate-50 h-[400px]">
             {/* Mock Notification */}
             <div className="bg-white p-3 rounded-2xl shadow-sm border border-gray-100 mb-4 animate-slide-down">
                 <div className="flex items-center gap-2 mb-2">
                   <div className="w-6 h-6 bg-blue-600 rounded-lg flex items-center justify-center text-white"><Shield className="w-3 h-3" /></div>
                   <div className="flex-1">
                     <div className="flex justify-between items-center">
                        <span className="text-[10px] font-black text-gray-900">Famitry</span>
                        <span className="text-[8px] text-gray-400">Now</span>
                     </div>
                     <p className="text-[9px] text-gray-500">Official Notification</p>
                   </div>
                 </div>
                 <div className={`p-2.5 rounded-xl text-[10px] leading-relaxed border ${
                   messageType === 'warning' ? 'bg-orange-50 border-orange-100 text-orange-900' :
                   messageType === 'alert' ? 'bg-red-50 border-red-100 text-red-900' :
                   'bg-indigo-50 border-indigo-100 text-indigo-900'
                 }`}>
                   {broadcastMsg || "Type to see how your message will look on user devices..."}
                 </div>
             </div>
          </div>
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-32 h-1 bg-gray-300 rounded-full"></div>
        </div>
      </div>
    </div>
  ), [broadcastMsg, targetAudience, messageType, isSending, members, handleBroadcast]);

  const MergeView = useCallback(() => {
    const allMembers = Object.values(members);
    const uniqueFamilies = [...new Set(allMembers.map(m => m.familyId))];

    const filteredMembers1 = mergeSearchMember1
      ? allMembers.filter(m => m.name.toLowerCase().includes(mergeSearchMember1.toLowerCase()))
      : [];

    const filteredMembers2 = mergeSearchMember2 && selectedMember1
      ? allMembers.filter(m => 
          m.name.toLowerCase().includes(mergeSearchMember2.toLowerCase()) &&
          m.familyId !== selectedMember1.familyId
        )
      : [];

    const relationshipOptions = [
      { value: 'father-son', label: 'Father → Son' },
      { value: 'father-daughter', label: 'Father → Daughter' },
      { value: 'mother-son', label: 'Mother → Son' },
      { value: 'mother-daughter', label: 'Mother → Daughter' },
      { value: 'husband-wife', label: 'Husband ↔ Wife' },
      { value: 'grandfather-grandson', label: 'Grandfather → Grandson' },
      { value: 'grandfather-granddaughter', label: 'Grandfather → Granddaughter' },
    ];

    return (
      <div className="space-y-6 animate-fade-in pb-20">
        {/* HEADER */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-16 -mt-16 blur-3xl"></div>
          <h3 className="font-bold text-2xl mb-2 flex items-center gap-3">
            <Users className="w-7 h-7" /> Merge Families
          </h3>
          <p className="text-indigo-100 text-sm mb-4 max-w-lg">
            Connect members from different families by establishing a relationship between them. All members from the source family will be merged into the target family.
          </p>
        </div>

        {/* MERGE FORM */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* MEMBER 1 SELECTION */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
            <h4 className="font-bold text-gray-900 mb-4 text-lg">Step 1: Select Member 1 (Target Family)</h4>
            
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Search Member</label>
                <input
                  type="text"
                  placeholder="Type name..."
                  value={mergeSearchMember1}
                  onChange={(e) => setMergeSearchMember1(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {mergeSearchMember1 && filteredMembers1.length > 0 && (
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-xl">
                  {filteredMembers1.map(m => (
                    <button
                      key={m.id}
                      onClick={() => {
                        setSelectedMember1(m);
                        setMergeSearchMember1(m.name);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-indigo-50 border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <div className="font-bold text-gray-900">{m.name}</div>
                      <div className="text-xs text-gray-500">FamilyID: {m.familyId}</div>
                    </button>
                  ))}
                </div>
              )}

              {selectedMember1 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                  <div className="font-bold text-indigo-900">{selectedMember1.name}</div>
                  <div className="text-xs text-indigo-700 font-mono mt-1">FamilyID: {selectedMember1.familyId}</div>
                  <button
                    onClick={() => {
                      setSelectedMember1(null);
                      setMergeSearchMember1('');
                    }}
                    className="text-xs text-indigo-600 font-bold mt-2 hover:text-indigo-800"
                  >
                    ✕ Change
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* MEMBER 2 SELECTION */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
            <h4 className="font-bold text-gray-900 mb-4 text-lg">Step 2: Select Member 2 (Source Family)</h4>
            
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Search Member</label>
                <input
                  type="text"
                  placeholder={selectedMember1 ? "Type name..." : "Select Member 1 first"}
                  value={mergeSearchMember2}
                  onChange={(e) => setMergeSearchMember2(e.target.value)}
                  disabled={!selectedMember1}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-50"
                />
              </div>

              {mergeSearchMember2 && filteredMembers2.length > 0 && (
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-xl">
                  {filteredMembers2.map(m => (
                    <button
                      key={m.id}
                      onClick={() => {
                        setSelectedMember2(m);
                        setMergeSearchMember2(m.name);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-purple-50 border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <div className="font-bold text-gray-900">{m.name}</div>
                      <div className="text-xs text-gray-500">FamilyID: {m.familyId}</div>
                    </button>
                  ))}
                </div>
              )}

              {selectedMember2 && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <div className="font-bold text-purple-900">{selectedMember2.name}</div>
                  <div className="text-xs text-purple-700 font-mono mt-1">FamilyID: {selectedMember2.familyId}</div>
                  <button
                    onClick={() => {
                      setSelectedMember2(null);
                      setMergeSearchMember2('');
                    }}
                    className="text-xs text-purple-600 font-bold mt-2 hover:text-purple-800"
                  >
                    ✕ Change
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RELATIONSHIP & PREVIEW */}
        {selectedMember1 && selectedMember2 && (
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
            <h4 className="font-bold text-gray-900 mb-6 text-lg">Step 3: Select Relationship</h4>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              {relationshipOptions.map(rel => (
                <button
                  key={rel.value}
                  onClick={() => setMergeRelationType(rel.value)}
                  className={`px-4 py-3 rounded-xl font-bold text-sm transition-all ${
                    mergeRelationType === rel.value
                      ? 'bg-indigo-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {rel.label}
                </button>
              ))}
            </div>

            {/* PREVIEW */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl p-6 mb-6">
              <div className="text-xs font-bold text-gray-500 uppercase mb-4 tracking-wider">Merge Preview</div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                <div className="text-center">
                  <div className="font-bold text-gray-900 text-lg mb-1">{selectedMember1.name}</div>
                  <div className="text-xs text-gray-600 font-mono">{selectedMember1.familyId}</div>
                </div>
                
                <div className="text-center">
                  <div className="text-indigo-600 font-black text-xl">→ {mergeRelationType.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} ←</div>
                </div>
                
                <div className="text-center">
                  <div className="font-bold text-gray-900 text-lg mb-1">{selectedMember2.name}</div>
                  <div className="text-xs text-gray-600 font-mono">{selectedMember2.familyId}</div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-indigo-300 space-y-2">
                <div className="text-sm text-gray-700">
                  <span className="font-bold">Result:</span> All members from <span className="font-mono bg-white px-2 py-1 rounded">{selectedMember2.familyId}</span> will be merged into <span className="font-mono bg-white px-2 py-1 rounded">{selectedMember1.familyId}</span>
                </div>
              </div>
            </div>

            {/* EXECUTE BUTTON */}
            <button
              onClick={handleMergeFamilies}
              disabled={isMerging}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-4 rounded-xl hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isMerging ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Merging...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" /> Execute Merge
                </>
              )}
            </button>
          </div>
        )}

        {/* MERGE LOG */}
        {mergeLog.length > 0 && (
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
            <h4 className="font-bold text-gray-900 mb-4 text-lg">Merge History</h4>
            
            <div className="space-y-4">
              {mergeLog.map((log, idx) => (
                <div key={idx} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                  <div className="flex justify-between items-start mb-3">
                    <div className="font-bold text-gray-900">{log.member1} ↔ {log.member2}</div>
                    <div className="text-xs text-gray-500">{new Date(log.timestamp).toLocaleString()}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
                    <div>Admin: <span className="font-bold">{log.admin}</span></div>
                    <div>Relationship: <span className="font-bold">{log.relationship}</span></div>
                    <div>Members Updated: <span className="font-bold text-indigo-600">{log.familiesAffected.membersUpdated}</span></div>
                    <div>Posts Updated: <span className="font-bold text-indigo-600">{log.familiesAffected.postsUpdated}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }, [mergeSearchMember1, mergeSearchMember2, selectedMember1, selectedMember2, mergeRelationType, isMerging, mergeLog, members, handleMergeFamilies]);

  // 3. GUARD CLAUSE: If not authorized, render NOTHING.
  if (!isAuthorized) {
      return null;
  }

  const InsightsView = () => (
    <div className="space-y-8 animate-fade-in pb-20">
      {/* KPI GRID */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-3xl border border-indigo-50 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity"><Users className="w-12 h-12 text-indigo-600" /></div>
          <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-2">Total Family</p>
          <h3 className="text-3xl font-black text-gray-900">{stats.totalMembers}</h3>
          <div className="text-xs text-indigo-600 font-bold flex items-center gap-1 mt-2"><MapPin className="w-3 h-3" /> {stats.cities} Cities Active</div>
        </div>
        <div className="bg-white p-5 rounded-3xl border border-purple-50 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity"><Activity className="w-12 h-12 text-purple-600" /></div>
          <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-2">Engagement</p>
          <h3 className="text-3xl font-black text-gray-900">{stats.engagementRate}%</h3>
          <div className="text-xs text-purple-600 font-bold mt-2">Monthly Active</div>
        </div>
        <div className="bg-white p-5 rounded-3xl border border-amber-50 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity"><Zap className="w-12 h-12 text-amber-600" /></div>
          <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-2">Unmarried Singles</p>
          <h3 className="text-xl font-black text-gray-900 mt-1">M: {stats.unmarriedMales} | F: {stats.unmarriedFemales} | O: {stats.unmarriedOthers}</h3>
          <div className="text-xs text-amber-600 font-bold mt-2">Eligible for Match</div>
        </div>
        <div className="bg-white p-5 rounded-3xl border border-red-50 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity"><AlertTriangle className="w-12 h-12 text-red-600" /></div>
          <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-2">Safety Alerts</p>
          <h3 className="text-3xl font-black text-gray-900">{stats.pendingComplaints}</h3>
          <div className="text-xs text-red-600 font-bold mt-2">Action Required</div>
        </div>
      </div>

      {/* FAMILY INSIGHTS */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" /> Family Insights
              </h3>
              <p className="text-xs text-gray-500 mt-1">All families sorted by member count</p>
            </div>
            <div className="bg-white px-4 py-2 rounded-full">
              <span className="text-sm font-black text-indigo-600">{familyInsights.totalFamilies} Families</span>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-6 py-4 text-left text-xs font-black text-gray-500 uppercase tracking-wider">Rank</th>
                <th className="px-6 py-4 text-left text-xs font-black text-gray-500 uppercase tracking-wider">Family ID</th>
                <th className="px-6 py-4 text-left text-xs font-black text-gray-500 uppercase tracking-wider">Members</th>
                <th className="px-6 py-4 text-left text-xs font-black text-gray-500 uppercase tracking-wider">Top Members</th>
              </tr>
            </thead>
            <tbody>
              {familyInsights.families.map((family, idx) => {
                const topMembers = family.members.slice(0, 3);
                const memberNames = topMembers.map(m => m.name).join(', ');
                const hasMore = family.memberCount > 3 ? ` +${family.memberCount - 3} more` : '';
                
                return (
                  <tr key={family.familyId} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 font-black text-sm">
                        {idx + 1}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-700">{family.familyId}</code>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-black text-gray-900">{family.memberCount}</span>
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"
                            style={{width: `${Math.min(100, (family.memberCount / (familyInsights.families[0]?.memberCount || 1)) * 100)}%`}}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {memberNames}{hasMore && <span className="text-gray-400 text-xs">{hasMore}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {familyInsights.families.length === 0 && (
          <div className="px-6 py-12 text-center">
            <p className="text-gray-500 text-sm">No families registered yet</p>
          </div>
        )}
      </div>

      {/* STRATEGIC SPLIT */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm min-h-[300px] flex flex-col">
           <div className="flex justify-between items-center mb-8">
             <div>
               <h3 className="text-lg font-black text-gray-900">Growth Trajectory</h3>
               <p className="text-xs text-gray-500">New member signups</p>
             </div>
             <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> +12%</span>
           </div>
           <div className="flex-1 flex items-end gap-3 w-full px-2">
              {[30, 45, 35, 60, 50, 80, 75, 90, 65, 85, 95, 100].map((h, i) => (
                <div key={i} className="flex-1 flex flex-col justify-end group cursor-pointer">
                   <div className="w-full bg-indigo-50 rounded-t-lg group-hover:bg-indigo-500 transition-all duration-300 relative" style={{height: `${h}%`}}>
                      <div className="opacity-0 group-hover:opacity-100 absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] px-2 py-1 rounded font-bold transition-opacity">
                        {h}
                      </div>
                   </div>
                </div>
              ))}
           </div>
        </div>

        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-black text-gray-900 mb-2">Generational Split</h3>
          <p className="text-xs text-gray-500 mb-6">Target: Increase Gen Z adoption</p>
          
          <div className="space-y-6">
             <div>
               <div className="flex justify-between text-xs font-bold mb-2">
                 <span className="text-purple-600 flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-purple-500"></div> Gen Z (Youth)</span>
                 <span>{Math.round((stats.genBreakdown.genZ / stats.totalMembers) * 100)}%</span>
               </div>
               <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-purple-500 rounded-full shadow-lg shadow-purple-200" style={{width: `${(stats.genBreakdown.genZ / stats.totalMembers) * 100}%`}}></div></div>
             </div>
             <div>
               <div className="flex justify-between text-xs font-bold mb-2">
                 <span className="text-indigo-600 flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-indigo-500"></div> Millennials</span>
                 <span>{Math.round((stats.genBreakdown.millennials / stats.totalMembers) * 100)}%</span>
               </div>
               <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-indigo-500 rounded-full shadow-lg shadow-indigo-200" style={{width: `${(stats.genBreakdown.millennials / stats.totalMembers) * 100}%`}}></div></div>
             </div>
             <div>
               <div className="flex justify-between text-xs font-bold mb-2">
                 <span className="text-blue-600 flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-blue-500"></div> Boomers & Gen X</span>
                 <span>{Math.round((stats.genBreakdown.boomers / stats.totalMembers) * 100)}%</span>
               </div>
               <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full shadow-lg shadow-blue-200" style={{width: `${(stats.genBreakdown.boomers / stats.totalMembers) * 100}%`}}></div></div>
             </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-black text-gray-900 mb-2">Gender Distribution</h3>
          <p className="text-xs text-gray-500 mb-6">Family composition by gender</p>
          
          <div className="space-y-6">
             <div>
               <div className="flex justify-between text-xs font-bold mb-2">
                 <span className="text-blue-600 flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-blue-500"></div> Male</span>
                 <span>{Math.round((stats.genderBreakdown.males / stats.totalMembers) * 100)}%</span>
               </div>
               <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full shadow-lg shadow-blue-200" style={{width: `${(stats.genderBreakdown.males / stats.totalMembers) * 100}%`}}></div></div>
             </div>
             <div>
               <div className="flex justify-between text-xs font-bold mb-2">
                 <span className="text-pink-600 flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-pink-500"></div> Female</span>
                 <span>{Math.round((stats.genderBreakdown.females / stats.totalMembers) * 100)}%</span>
               </div>
               <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-pink-500 rounded-full shadow-lg shadow-pink-200" style={{width: `${(stats.genderBreakdown.females / stats.totalMembers) * 100}%`}}></div></div>
             </div>
             <div>
               <div className="flex justify-between text-xs font-bold mb-2">
                 <span className="text-purple-600 flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-purple-500"></div> Other</span>
                 <span>{Math.round((stats.genderBreakdown.others / stats.totalMembers) * 100)}%</span>
               </div>
               <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-purple-500 rounded-full shadow-lg shadow-purple-200" style={{width: `${(stats.genderBreakdown.others / stats.totalMembers) * 100}%`}}></div></div>
             </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-black text-gray-900 mb-2">Marital Status Distribution</h3>
          <p className="text-xs text-gray-500 mb-6">Family composition by marital status</p>
          
          <div className="space-y-6">
             <div>
               <div className="flex justify-between text-xs font-bold mb-2">
                 <span className="text-indigo-600 flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-indigo-500"></div> Single</span>
                 <span>{Math.round((stats.maritalBreakdown.single / stats.totalMembers) * 100)}%</span>
               </div>
               <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-indigo-500 rounded-full shadow-lg shadow-indigo-200" style={{width: `${(stats.maritalBreakdown.single / stats.totalMembers) * 100}%`}}></div></div>
             </div>
             <div>
               <div className="flex justify-between text-xs font-bold mb-2">
                 <span className="text-pink-600 flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-pink-500"></div> Married</span>
                 <span>{Math.round((stats.maritalBreakdown.married / stats.totalMembers) * 100)}%</span>
               </div>
               <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-pink-500 rounded-full shadow-lg shadow-pink-200" style={{width: `${(stats.maritalBreakdown.married / stats.totalMembers) * 100}%`}}></div></div>
             </div>
             <div>
               <div className="flex justify-between text-xs font-bold mb-2">
                 <span className="text-red-600 flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-red-500"></div> Divorced</span>
                 <span>{Math.round((stats.maritalBreakdown.divorced / stats.totalMembers) * 100)}%</span>
               </div>
               <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-red-500 rounded-full shadow-lg shadow-red-200" style={{width: `${(stats.maritalBreakdown.divorced / stats.totalMembers) * 100}%`}}></div></div>
             </div>
          </div>
        </div>
      </div>

      {/* SYSTEM PULSE & CONTENT */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
           <h3 className="text-lg font-black text-gray-900 mb-6">Content Ecosystem</h3>
           <div className="flex gap-4 items-center">
              <div className="flex-1 bg-gradient-to-br from-pink-50 to-rose-50 p-6 rounded-2xl text-center border border-pink-100">
                 <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                    <ImageIcon className="w-6 h-6 text-pink-500" />
                 </div>
                 <div className="text-2xl font-black text-gray-900">{stats.contentMix.media}%</div>
                 <div className="text-[10px] font-bold text-pink-400 uppercase tracking-wider">Media Rich</div>
              </div>
              <div className="flex-1 bg-gray-50 p-6 rounded-2xl text-center border border-gray-100">
                 <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                    <MessageSquare className="w-6 h-6 text-gray-500" />
                 </div>
                 <div className="text-2xl font-black text-gray-900">{stats.contentMix.text}%</div>
                 <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Text Only</div>
              </div>
           </div>
        </div>

        <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800 shadow-2xl text-white relative overflow-hidden">
           <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full blur-3xl"></div>
           <h3 className="text-lg font-bold mb-6 flex items-center gap-2 relative z-10"><Activity className="w-5 h-5 text-green-400" /> System Pulse</h3>
           <div className="space-y-5 relative z-10">
              <div className="flex items-center justify-between">
                 <div className="flex items-center gap-3 text-sm text-slate-300"><Database className="w-4 h-4" /> Database Storage</div>
                 <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden"><div className="h-full bg-green-500 w-[45%]"></div></div>
                    <span className="text-sm font-bold text-white">45%</span>
                 </div>
              </div>
              <div className="flex items-center justify-between">
                 <div className="flex items-center gap-3 text-sm text-slate-300"><Server className="w-4 h-4" /> API Latency</div>
                 <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    <span className="text-sm font-bold text-white">12ms</span>
                 </div>
              </div>
              <div className="flex items-center justify-between">
                 <div className="flex items-center gap-3 text-sm text-slate-300"><Smartphone className="w-4 h-4" /> Mobile Usage</div>
                 <div className="text-sm font-bold text-blue-400">88%</div>
              </div>
           </div>
        </div>
      </div>
    </div>
  );

  const ComplaintsView = () => (
    <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden animate-fade-in pb-20">
      <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
         <h2 className="text-lg font-black text-gray-900">Incoming Reports</h2>
         <div className="flex gap-2">
            <button className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"><Filter className="w-4 h-4 text-gray-500" /></button>
            <button className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"><MoreVertical className="w-4 h-4 text-gray-500" /></button>
         </div>
      </div>
      <div className="divide-y divide-gray-100">
        {complaints.length > 0 ? complaints.map(c => (
          <div key={c.id} className="p-5 hover:bg-gray-50 transition-colors flex flex-col gap-3 group">
             <div className="flex justify-between items-start">
               <div className="flex gap-4">
                 <div className={`mt-1.5 w-3 h-3 rounded-full shadow-sm ring-2 ring-white ${c.status === 'Pending' ? 'bg-orange-500' : 'bg-green-500'}`}></div>
                 <div>
                   <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-gray-900 text-sm">Report ID: {c.id.slice(0,5)}</span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${c.severity === 'High' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{c.severity}</span>
                   </div>
                   <p className="text-xs text-gray-500 font-medium">Reported by {c.reporterName} • {c.reason}</p>
                 </div>
               </div>
               
               {c.status === 'Pending' ? (
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleComplaintAction(c.id, 'resolve')} className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-lg text-xs font-bold hover:bg-green-100"><CheckCircle className="w-3 h-3" /> Resolve</button>
                    <button onClick={() => handleComplaintAction(c.id, 'ban')} className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs font-bold hover:bg-red-100"><XCircle className="w-3 h-3" /> Ban</button>
                  </div>
               ) : (
                  <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-3 py-1 rounded-full border border-gray-200">{c.status}</span>
               )}
             </div>

             {/* EVIDENCE BOX: Shows what was reported */}
             {(c.contentPreview || c.imagePreview) && (
               <div className="ml-7 bg-white border border-gray-200 p-3 rounded-xl shadow-sm text-sm text-gray-700">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">Evidence Snapshot</p>
                  {c.contentPreview && <p className="mb-2 italic">"{c.contentPreview}"</p>}
                  {c.imagePreview && <img src={c.imagePreview} className="w-20 h-20 object-cover rounded-lg border border-gray-100" />}
               </div>
             )}
          </div>
        )) : (
          <div className="p-12 text-center">
             <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-8 h-8 text-green-500" /></div>
             <h3 className="text-gray-900 font-bold">All Clear!</h3>
             <p className="text-gray-500 text-sm">No pending complaints at the moment.</p>
          </div>
        )}
      </div>
    </div>
  );

  const AccessControlView = () => (
    <div className="space-y-6 animate-fade-in pb-20">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-16 -mt-16 blur-3xl"></div>
        <h3 className="font-bold text-xl mb-4 flex items-center gap-3"><Shield className="w-6 h-6" /> Super Admin Status</h3>
        <p className="text-blue-100 text-sm mb-6 max-w-lg">These users have root access to the entire Famitry database. This list is immutable and hardcoded for security.</p>
        <div className="flex flex-wrap gap-3">
          {SUPER_ADMINS.map(num => (
             <div key={num} className="px-4 py-2 bg-white/20 backdrop-blur-md rounded-xl text-sm font-mono flex items-center gap-2 border border-white/10">
               <Shield className="w-3 h-3 text-green-300" /> {num}
             </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
           <div>
             <h2 className="text-lg font-black text-gray-900">Access Requests</h2>
             <p className="text-xs text-gray-500">Admins require double-approval protocol</p>
           </div>
           <span className="bg-orange-50 text-orange-600 px-3 py-1 rounded-full text-xs font-bold border border-orange-100">
             {adminRequests.filter(r => r.status === 'Pending').length} Pending
           </span>
        </div>
        
        <div className="divide-y divide-gray-100">
          {adminRequests.map(req => (
              <div key={req.id} className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center font-bold text-indigo-600">{req.name[0]}</div>
                    <div>
                        <div className="font-bold text-gray-900 flex items-center gap-2">
                            {req.name} 
                            {req.status === 'Approved' && <CheckCircle className="w-4 h-4 text-green-500" />}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">{req.phone} • Requested {req.date}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 bg-gray-50 p-2 rounded-xl border border-gray-100">
                     <div className="flex flex-col items-end px-2">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Protocol</span>
                        <span className={`text-xs font-black ${req.approvals >= 2 ? 'text-green-600' : 'text-orange-600'}`}>
                           {req.approvals} / 2 Approved
                        </span>
                     </div>
                     {req.status === 'Pending' ? (
                        <button 
                           onClick={() => handleAdminApproval(req.id)} 
                           className="px-4 py-2 bg-green-600 text-white rounded-lg text-xs font-bold hover:bg-green-700 shadow-sm active:scale-95 transition-all"
                        >
                           Approve Request
                        </button>
                     ) : (
                        <span className="px-4 py-2 bg-green-100 text-green-700 rounded-lg text-xs font-bold border border-green-200">
                           Access Granted
                        </span>
                     )}
                  </div>
              </div>
          ))}
        </div>
      </div>
    </div>
  );

  // --- LAYOUT RENDER ---
  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-slate-50">
      
      {/* 1. MOBILE HEADER (Horizontal Nav) */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-slate-900 z-50 text-white shadow-lg">
        <div className="flex items-center justify-between p-4 pb-2">
          <h1 className="text-lg font-black tracking-tighter">Admin<span className="text-indigo-400">.</span></h1>
          <Shield className="w-5 h-5 text-indigo-400" />
        </div>
        <div className="flex overflow-x-auto no-scrollbar px-2 pb-2 gap-2">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveView(item.id)}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap ${
                activeView === item.id 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-slate-800 text-slate-400'
              }`}
            >
              <item.icon className="w-3 h-3" />
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* 2. DESKTOP SIDEBAR */}
      <div className="hidden md:flex w-72 bg-slate-900 text-white flex-col fixed h-full z-10 shadow-2xl">
        <div className="p-8 mb-4">
          <h1 className="text-3xl font-black tracking-tighter">Admin<span className="text-indigo-400">.</span></h1>
          <p className="text-xs text-slate-500 mt-1 uppercase tracking-widest font-bold">Command Center</p>
        </div>
        <nav className="space-y-3 px-4 flex-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveView(item.id)}
              className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl transition-all group relative overflow-hidden ${
                activeView === item.id 
                  ? 'bg-indigo-600 text-white font-bold shadow-lg shadow-indigo-900/50' 
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              }`}
            >
              {activeView === item.id && <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-400"></div>}
              <item.icon className={`w-5 h-5 ${activeView === item.id ? 'text-white' : 'text-slate-500 group-hover:text-white transition-colors'}`} />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>
        
        {/* Admin Profile Snippet */}
        <div className="p-6 mt-auto bg-slate-950/50 border-t border-slate-800">
           <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 flex items-center justify-center font-bold text-sm shadow-lg">SA</div>
              <div>
                 <div className="text-sm font-bold text-white">Super Admin</div>
                 <div className="text-[10px] text-green-400 flex items-center gap-1"><div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div> System Active</div>
              </div>
           </div>
        </div>
      </div>

      {/* 3. MAIN CONTENT */}
      <div className="flex-1 md:ml-72 p-4 pt-32 md:p-12 overflow-y-auto overflow-x-hidden w-full min-h-screen max-w-full">
        <header className="hidden md:flex justify-between items-center mb-10 animate-fade-in-down">
          <div>
            <h1 className="text-3xl font-black text-gray-900 capitalize tracking-tight">{activeView}</h1>
            <p className="text-sm text-gray-500 mt-1 font-medium">Overview of system performance & alerts</p>
          </div>
          <div className="flex gap-3">
             <button className="bg-white p-3 rounded-full border border-gray-200 shadow-sm text-gray-400 hover:text-indigo-600 hover:border-indigo-100 transition-all relative">
                <Bell className="w-5 h-5" />
                {stats.pendingComplaints > 0 && <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></span>}
             </button>
             <div className="bg-white pl-4 pr-2 py-2 rounded-full border border-gray-200 shadow-sm flex items-center gap-2 w-64 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
                <input type="text" placeholder="Search system..." className="bg-transparent outline-none text-sm w-full placeholder:text-gray-400" />
                <div className="bg-gray-100 p-1.5 rounded-full"><Search className="w-4 h-4 text-gray-500" /></div>
             </div>
          </div>
        </header>

        {activeView === 'insights' && <InsightsView />}
        {activeView === 'analytics' && currentUser && <Analytics familyId={currentUser.familyId} />}
        {activeView === 'broadcast' && BroadcastView()}
        {activeView === 'merge' && MergeView()}
        {activeView === 'complaints' && <ComplaintsView />}
        {activeView === 'access' && <AccessControlView />}
      </div>
    </div>
  );
};

export default AdminDashboard;